import { useState } from "react";
import { Drawer } from "@/components/ui/Drawer";
import { Button } from "@/components/ui/Button";
import { Textarea } from "@/components/ui/Textarea";
import { Input } from "@/components/ui/Input";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { LoadingState } from "@/components/feedback/LoadingState";
import { ErrorState } from "@/components/feedback/ErrorState";
import { useRefundPayment, useRide } from "@/features/rides/useRides";
import { toast } from "@/store/toast.store";
import { formatDateTime, formatINR } from "@/utils/format";

export function RideDetailDrawer({ rideId, onClose }: { rideId: string | null; onClose: () => void }) {
  const ride = useRide(rideId ?? undefined);
  const refund = useRefundPayment();
  const [refundingPaymentId, setRefundingPaymentId] = useState<string | null>(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");

  return (
    <Drawer
      open={!!rideId}
      onClose={onClose}
      title={ride.data ? `Ride ${ride.data.code}` : "Ride"}
      subtitle={ride.data?.statusLabel}
    >
      {ride.isLoading && <LoadingState label="Loading ride…" />}
      {ride.error && <ErrorState error={ride.error} onRetry={() => ride.refetch()} />}
      {ride.data && (
        <div className="flex flex-col gap-5">
          <div className="flex items-center gap-2">
            <StatusBadge status={ride.data.status} label={ride.data.statusLabel} alert={ride.data.alert} />
            <span className="text-sm text-ink-muted">{formatDateTime(ride.data.createdAt)}</span>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-ink-faint">Rider</p>
              <p className="font-medium text-ink">{ride.data.rider}</p>
              {ride.data.riderPhone && <p className="text-ink-faint">{ride.data.riderPhone}</p>}
            </div>
            <div>
              <p className="text-xs text-ink-faint">Driver</p>
              <p className="font-medium text-ink">{ride.data.driver}</p>
              {ride.data.vehicleNumber && <p className="text-ink-faint">{ride.data.vehicleNumber}</p>}
            </div>
            <div>
              <p className="text-xs text-ink-faint">Pickup</p>
              <p className="text-ink">{ride.data.pickup.name ?? ride.data.pickup.area ?? "—"}</p>
            </div>
            <div>
              <p className="text-xs text-ink-faint">Drop</p>
              <p className="text-ink">{ride.data.drop.name ?? ride.data.drop.area ?? "—"}</p>
            </div>
            <div>
              <p className="text-xs text-ink-faint">Fare</p>
              <p className="font-medium text-ink">
                {ride.data.fare !== undefined ? formatINR(ride.data.fare) : "—"}
              </p>
            </div>
            <div>
              <p className="text-xs text-ink-faint">Distance</p>
              <p className="text-ink">
                {ride.data.distanceKm !== undefined ? `${ride.data.distanceKm.toFixed(1)} km` : "—"}
              </p>
            </div>
          </div>

          {ride.data.timeline.length > 0 && (
            <div>
              <h4 className="mb-2 text-sm font-semibold text-ink">Timeline</h4>
              <ul className="flex flex-col gap-1.5 text-sm">
                {ride.data.timeline.map((t, i) => (
                  <li key={i} className="flex items-center justify-between text-ink-muted">
                    <span>{t.label}</span>
                    {t.at && <span className="text-xs text-ink-faint">{formatDateTime(t.at)}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <h4 className="mb-2 text-sm font-semibold text-ink">Payments</h4>
            {ride.data.payments.length === 0 && <p className="text-xs text-ink-faint">No payment records.</p>}
            <ul className="flex flex-col gap-2">
              {ride.data.payments.map((p) => (
                <li
                  key={p.id}
                  className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm"
                >
                  <div>
                    <p className="font-medium text-ink">{formatINR(p.amount)}</p>
                    <p className="text-xs text-ink-faint">
                      {p.method} · {p.status}
                      {p.refundedAmount > 0 ? ` · refunded ${formatINR(p.refundedAmount)}` : ""}
                    </p>
                  </div>
                  {p.status !== "REFUNDED" && (
                    <Button variant="outline" size="sm" onClick={() => setRefundingPaymentId(p.id)}>
                      Refund
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {refundingPaymentId && (
            <div className="flex flex-col gap-2 rounded-lg border border-border p-3">
              <p className="text-sm font-medium text-ink">Refund payment</p>
              <Input
                type="number"
                placeholder="Amount (leave blank for full remaining amount)"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
              <Textarea
                placeholder="Reason (required)"
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setRefundingPaymentId(null);
                    setAmount("");
                    setReason("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  loading={refund.isPending}
                  disabled={reason.trim().length === 0}
                  onClick={() =>
                    refund.mutate(
                      {
                        paymentId: refundingPaymentId,
                        rideId: ride.data.id,
                        amount: amount ? Number(amount) : undefined,
                        reason: reason.trim(),
                      },
                      {
                        onSuccess: () => {
                          toast.success("Refund issued");
                          setRefundingPaymentId(null);
                          setAmount("");
                          setReason("");
                        },
                      },
                    )
                  }
                >
                  Issue refund
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}
